'use client';

import React from 'react';
import { Language, TranslationDictionary } from '@monorepo/types';

type TranslateOptions = {
  fallback?: string;
  values?: Record<string, string | number>;
};

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, options?: TranslateOptions) => string;
  translateLiteral: (text?: string | null, fallbackKey?: string) => string;
};

const BASE_TRANSLATIONS: TranslationDictionary = {
  de: {
    'header.add': 'Hinzufügen',
    'header.actions': 'Aktionen',
    'header.commands': 'Befehle',
    'header.commandHint': 'Befehlspalette öffnen',
    'sidebar.signedIn': 'Angemeldet:',
    'sidebar.deleteConfirmation': 'Soll "{view}" wirklich gelöscht werden?',
    'commandFooter.actions': 'Aktionen',
    'commandFooter.open': 'Öffnen',
    'commandFooter.save': 'Speichern',
    'table.fields': 'Felder',
    'table.sortAscending': 'Aufsteigend',
    'table.sortDescending': 'Absteigend',
    'table.hideColumn': 'Ausblenden',
    'filter.selectAll': 'Alle auswählen',
    'filter.clearAll': 'Alle abwählen',
    'filter.reset': 'Zurücksetzen',
    'filter.selectedCount': '{count} ausgewählt',
    'filter.clearFilters': 'Filter zurücksetzen',
    'filter.searchPlaceholder': '{field} durchsuchen',
    'toggle.on': 'An',
    'toggle.off': 'Aus',
    'toggle.reset': 'Zurücksetzen',
  },
  en: {
    'header.add': 'Add',
    'header.actions': 'Actions',
    'header.commands': 'Commands',
    'header.commandHint': 'Open command palette',
    'sidebar.signedIn': 'Signed in:',
    'sidebar.deleteConfirmation': 'Are you sure you want to delete "{view}"?',
    'commandFooter.actions': 'Actions',
    'commandFooter.open': 'Open',
    'commandFooter.save': 'Save',
    'table.fields': 'Fields',
    'table.sortAscending': 'Sort ascending',
    'table.sortDescending': 'Sort descending',
    'table.hideColumn': 'Hide column',
    'filter.selectAll': 'Select all',
    'filter.clearAll': 'Clear all',
    'filter.reset': 'Reset',
    'filter.selectedCount': '{count} selected',
    'filter.clearFilters': 'Clear filters',
    'filter.searchPlaceholder': 'Search {field}',
    'toggle.on': 'On',
    'toggle.off': 'Off',
    'toggle.reset': 'Reset',
  },
};

const BASE_LITERAL_TRANSLATIONS: TranslationDictionary = {
  de: {},
  en: {},
};

const LANGUAGES: Language[] = ['de', 'en'];

const mergeDictionaries = (
  base: TranslationDictionary,
  extra?: TranslationDictionary
): TranslationDictionary => {
  return LANGUAGES.reduce((acc, lang) => {
    acc[lang] = {
      ...(base[lang] || {}),
      ...(extra?.[lang] || {}),
    };
    return acc;
  }, {} as TranslationDictionary);
};

const I18nContext = React.createContext<I18nContextValue | undefined>(
  undefined
);

const STORAGE_KEY = 'admints:language';

export const I18nProvider = ({
  children,
  dictionary,
  literalDictionary,
}: {
  children: React.ReactNode;
  dictionary?: TranslationDictionary;
  literalDictionary?: TranslationDictionary;
}) => {
  const [language, setLanguage] = React.useState<Language>('de');

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en') {
      setLanguage('en');
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const mergedDictionary = React.useMemo(
    () => mergeDictionaries(BASE_TRANSLATIONS, dictionary),
    [dictionary]
  );

  const mergedLiteralDictionary = React.useMemo(
    () => mergeDictionaries(BASE_LITERAL_TRANSLATIONS, literalDictionary),
    [literalDictionary]
  );

  const translate = React.useCallback(
    (key: string, options?: TranslateOptions) => {
      const activeDict = mergedDictionary[language] || {};
      const fallbackDict = mergedDictionary.de || {};

      let template =
        activeDict[key] ?? fallbackDict[key] ?? options?.fallback ?? key;

      if (options?.values) {
        Object.entries(options.values).forEach(([token, value]) => {
          template = template.replace(
            new RegExp(`{${token}}`, 'g'),
            String(value)
          );
        });
      }

      return template;
    },
    [language, mergedDictionary]
  );

  const translateLiteral = React.useCallback(
    (text?: string | null, fallbackKey?: string) => {
      if (!text) return '';
      const literalDict = mergedLiteralDictionary[language] || {};
      const fallbackLiteralDict = mergedLiteralDictionary.de || {};

      if (literalDict[text]) return literalDict[text];
      if (fallbackLiteralDict[text]) return fallbackLiteralDict[text];
      if (fallbackKey) return translate(fallbackKey, { fallback: text });
      return text;
    },
    [language, mergedLiteralDictionary, translate]
  );

  const value = React.useMemo(
    () => ({
      language,
      setLanguage,
      t: translate,
      translateLiteral,
    }),
    [language, translate, translateLiteral]
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = React.useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  return context;
};
